'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [form, setForm] = useState({
    eventType: '',
    eventDate: '',
    name: '',
    contact: '',
    details: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.eventType && form.name && form.contact) {
      setSubmitted(true);
    }
  };

  return (
    <div
      className={`modal-backdrop ${isOpen ? 'open' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Book a shoot inquiry form"
    >
      <div className="modal-panel w-full sm:max-w-xl sm:mx-4 self-end sm:self-auto max-h-[92vh] overflow-y-auto">
        {/* Glassmorphic panel */}
        <div
          className="relative border border-parchment/10 p-6 sm:p-10 md:p-14"
          style={{
            background: 'rgba(13,13,13,0.96)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
          }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-parchment/30 hover:text-gold transition-colors duration-300 p-2 -m-2 touch-manipulation"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={22} variant="outline" />
          </button>

          {/* Gold rule */}
          <div className="w-8 h-px bg-gold mb-6 sm:mb-8" />

          {!submitted ? (
            <>
              <h3 className="font-serif text-parchment text-xl sm:text-2xl md:text-3xl font-light leading-tight mb-2">
                Book a Shoot
              </h3>
              <p className="text-parchment/35 text-xs tracking-wide font-light mb-8 sm:mb-10">
                Tell me about your event and I&apos;ll get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-7">
                {/* Event Type */}
                <div>
                  <label className="block text-parchment/30 text-[0.62rem] tracking-[0.16em] uppercase mb-3">
                    Event Type
                  </label>
                  <div className="relative">
                    <select
                      required
                      value={form.eventType}
                      onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                      className="ink-select"
                    >
                      <option value="" disabled>Select event type</option>
                      <option value="car-meet">Car Meet</option>
                      <option value="car-show">Car Show</option>
                      <option value="rally">Rally / Road Trip</option>
                      <option value="track-day">Track Day</option>
                      <option value="build-shoot">Build / Detail Shoot</option>
                      <option value="social">Social &amp; Lifestyle</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="absolute right-0 bottom-3 pointer-events-none">
                      <Icon name="ChevronDownIcon" size={14} variant="outline" className="text-parchment/30" />
                    </div>
                  </div>
                </div>

                {/* Event Date */}
                <div>
                  <label className="block text-parchment/30 text-[0.62rem] tracking-[0.16em] uppercase mb-3">
                    Event Date
                  </label>
                  <input
                    type="date"
                    value={form.eventDate}
                    onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                    className="ink-input"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-parchment/30 text-[0.62rem] tracking-[0.16em] uppercase mb-3">
                    Your Name
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="First and last name"
                    className="ink-input"
                  />
                </div>

                {/* Contact */}
                <div>
                  <label className="block text-parchment/30 text-[0.62rem] tracking-[0.16em] uppercase mb-3">
                    Email or Instagram
                  </label>
                  <input
                    type="text"
                    required
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    placeholder="your@email.com or @handle"
                    className="ink-input"
                  />
                </div>

                {/* Details */}
                <div>
                  <label className="block text-parchment/30 text-[0.62rem] tracking-[0.16em] uppercase mb-3">
                    Tell me about the shoot
                  </label>
                  <textarea
                    rows={3}
                    value={form.details}
                    onChange={(e) => setForm({ ...form, details: e.target.value })}
                    placeholder="Location, expected turnout, what you need covered..."
                    className="ink-input resize-none"
                    style={{ lineHeight: '1.7' }}
                  />
                </div>

                <button type="submit" className="gold-btn w-full text-center touch-manipulation">
                  <span>Send Inquiry</span>
                </button>
              </form>
            </>
          ) : (
            <div className="py-8 text-center">
              <div className="w-8 h-px bg-gold mx-auto mb-8" />
              <p className="font-serif text-parchment text-2xl font-light mb-4">
                Got it.
              </p>
              <p className="text-parchment/45 text-sm font-light leading-relaxed">
                I&apos;ll be in touch within 24 hours.
                <br />
                See you at the shoot.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}